import { useUnits } from '../../units/UnitContext';
import { formatDualDimension } from '../../units/unitConversions';
import FormedThreadProfile from './FormedThreadProfile';

const Dimension = ({ value, primary = false }) => {
  const { unitSystem } = useUnits();
  const formatted = formatDualDimension(value, unitSystem);
  return (
    <span className={primary ? 'metricPreDrillPrimaryValue' : undefined}>
      <strong>{formatted.primary}</strong>
      <span className="metricSecondaryDimension">{formatted.secondary}</span>
    </span>
  );
};

const SignedDimension = ({ value }) => {
  const { unitSystem } = useUnits();
  const formatted = formatDualDimension(Math.abs(Number(value)), unitSystem);
  const sign = Number(value) > 0 ? '+' : Number(value) < 0 ? '−' : '';
  return <><strong>{sign}{formatted.primary}</strong><span className="metricSecondaryDimension">{sign}{formatted.secondary}</span></>;
};

const statusPresentation = Object.freeze({
  LOW_FORMATION: {
    label: 'Below typical range',
    className: 'is-low',
    message: 'A larger pre-drill diameter produces a less developed thread profile and may reduce thread load capacity. Suitability should be evaluated for the application.',
  },
  TYPICAL_RANGE: {
    label: 'Typical formation range',
    className: 'is-typical',
    message: 'The estimated formation is within the indicative 55–70% range. This reference range is not a mandatory manufacturing limit.',
  },
  HIGH_FORMATION: {
    label: 'Above typical range',
    className: 'is-high',
    message: 'A smaller pre-drill diameter increases material deformation and forming torque, which may increase tool loading, wear and tap-failure risk. Greater formation does not necessarily provide a proportional strength increase.',
  },
});

const CuttingResults = ({ cutting }) => (
  <div className="metricPreDrillCard">
    <h4>Cutting Tap / Thread Milling</h4>
    <div className="metricPreDrillLayout">
      <div className="metricPreDrillRecommendation">
        <span>Recommended pre-drill</span>
        <Dimension value={cutting.recommendedPreDrillDiameterMm} primary />
      </div>
      <div>
        <h5>Finished minor diameter D1</h5>
        <dl className="metricLimits">
          <div><dt>Minimum</dt><dd><Dimension value={cutting.finishedMinorDiameterD1LimitsMm?.minimumMm} /></dd></div>
          <div><dt>Maximum</dt><dd><Dimension value={cutting.finishedMinorDiameterD1LimitsMm?.maximumMm} /></dd></div>
        </dl>
      </div>
    </div>
    <p className="metricPreDrillWarning" role="note">
      Finished-thread D1 limits describe the completed internal thread. They are not guaranteed manufacturing tolerance limits for the pre-drilled hole.
    </p>
  </div>
);

const FormingResults = ({ result, formingDiameterInput, onFormingDiameterChange, loading, error }) => {
  const forming = result.forming;
  const range = forming.indicativeFormationRange;
  const status = statusPresentation[forming.formationStatus] || statusPresentation.TYPICAL_RANGE;
  const recommendedInput = String(forming.recommendedPreDrillDiameterMm);
  const inputComplete = /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(formingDiameterInput.trim())
    && Number(formingDiameterInput) > 0;

  return (
    <div className="metricPreDrillCard metricFormingCard">
      <h4>Forming Tap</h4>
      <div className="metricFormationReferences">
        <section className="metricFormationBoundary">
          <p>Min formation</p>
          <strong>{range.minimumFormationPercent}% formation</strong>
          <span>Pre-drill diameter</span>
          <Dimension value={range.diameterAtMinimumFormationMm} />
          <small>Larger hole · lower formation</small>
        </section>

        <section className="metricFormationSelected">
          <p>Recommended</p>
          <div className="metricFormingRecommendation">
            <span>Recommended pre-drill</span>
            <Dimension value={forming.recommendedPreDrillDiameterMm} primary />
          </div>
          <div className="metricFormingInputRow">
            <label htmlFor="metric-forming-pre-drill">Selected pre-drill diameter [mm]</label>
            <div>
              <input
                id="metric-forming-pre-drill"
                type="text"
                inputMode="decimal"
                value={formingDiameterInput}
                onChange={event => onFormingDiameterChange?.(event.target.value)}
                aria-describedby="metric-forming-input-help"
              />
              <span>mm</span>
              <button type="button" onClick={() => onFormingDiameterChange?.(recommendedInput)}>Reset</button>
            </div>
            <small id="metric-forming-input-help">
              {inputComplete ? 'The backend recalculates formation and geometry after a short pause.' : 'Enter a complete positive diameter; incomplete input is kept without submitting.'}
            </small>
          </div>
          <dl className="metricFormingSummary">
            <div><dt>Estimated formation</dt><dd><strong>Approximately {Number(forming.estimatedFormationPercent).toFixed(1)}%</strong></dd></div>
            <div><dt>Difference from recommendation</dt><dd><SignedDimension value={forming.differenceFromRecommendationMm} /></dd></div>
          </dl>
          {loading && <p className="metricFormingUpdate" role="status">Updating formation and geometry…</p>}
          {error && <p className="metricFormingUpdate is-error" role="status">{error} The last valid result remains displayed.</p>}
        </section>

        <section className="metricFormationBoundary">
          <p>Max formation</p>
          <strong>{range.maximumFormationPercent}% formation</strong>
          <span>Pre-drill diameter</span>
          <Dimension value={range.diameterAtMaximumFormationMm} />
          <small>Smaller hole · higher formation</small>
        </section>
      </div>

      <p className="metricFormationInverseNote">
        Formation percentage and drill diameter have an inverse relationship. The 55% and 70% positions are indicative references, not mandatory hole limits.
      </p>
      <section className="metricFormingD1Reference" aria-labelledby="metric-forming-d1-title">
        <h5 id="metric-forming-d1-title">Finished-thread D1 references</h5>
        <dl className="metricLimits">
          <div><dt>Minimum D1</dt><dd><Dimension value={result.referenceThread?.finishedMinorDiameterD1LimitsMm?.minimumMm} /></dd></div>
          <div><dt>Maximum D1</dt><dd><Dimension value={result.referenceThread?.finishedMinorDiameterD1LimitsMm?.maximumMm} /></dd></div>
        </dl>
        <p>These are normative finished-thread tolerance references. The estimated profile does not demonstrate manufacturing compliance.</p>
      </section>
      <div className={`metricFormationStatus ${status.className}`} role="note">
        <strong>{status.label}</strong>
        <p>{status.message}</p>
      </div>
      <FormedThreadProfile profile={forming.estimatedProfile} designation={result.designation} />
    </div>
  );
};

const PreDrillResults = ({
  result,
  loading = false,
  error = '',
  formingDiameterInput = '',
  onFormingDiameterChange,
  expandedTechnology = null,
}) => {
  const cutting = result?.cutting;
  const forming = result?.forming;
  const hasCuttingRecommendation = Number.isFinite(Number(cutting?.recommendedPreDrillDiameterMm));
  const hasFormingRecommendation = Number.isFinite(Number(forming?.recommendedPreDrillDiameterMm));
  const hasResult = hasCuttingRecommendation || hasFormingRecommendation;

  if (!expandedTechnology && hasResult) return null;

  return (
    <section className="metricPreDrill" aria-label="Pre-drill diameter">
      {loading && !hasResult && <p className="metricPreDrillMessage" role="status">Loading pre-drill recommendations…</p>}
      {!loading && error && !hasResult && (
        <p className="metricPreDrillMessage is-error" role="status">
          {error} The thread dimensions above remain valid and available.
        </p>
      )}
      {!loading && !error && !hasResult && (
        <p className="metricPreDrillMessage" role="status">No pre-drill recommendation is available for this thread.</p>
      )}
      {expandedTechnology === 'cutting' && hasCuttingRecommendation && (
        <div id="metric-cutting-pre-drill-results">
          <CuttingResults cutting={cutting} />
        </div>
      )}
      {expandedTechnology === 'forming' && hasFormingRecommendation && (
        <div id="metric-forming-pre-drill-results">
          <FormingResults
            result={result}
            formingDiameterInput={formingDiameterInput}
            onFormingDiameterChange={onFormingDiameterChange}
            loading={loading}
            error={error}
          />
        </div>
      )}
    </section>
  );
};

export default PreDrillResults;
